import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Heart,
  FileText,
  Upload,
  AlertTriangle,
  Calendar,
  Scale,
  FileUp,
  X,
  Loader2,
  CheckCircle,
  Clock,
  Gavel,
  Home,
  ClipboardList,
  FileSearch,
  CalendarClock,
  ListTodo,
  FolderOpen,
  PenTool,
  Building,
  Hash,
  User,
  Download,
  Copy,
  Shield,
  UserCheck,
  FileEdit,
  AlertCircle,
  ChevronRight,
  Phone,
  Mail,
  Send,
  ArrowRight,
  Search,
  ExternalLink,
  Info,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  FileWarning,
  CheckCircle2,
  XCircle,
  HelpCircle,
  AlertOctagon,
  FileDown,
  Link2
} from "lucide-react";
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import { ScrollArea } from "../components/ui/scroll-area";
import { PartyHealthCheckSkeleton } from "../components/skeletons/PartyHealthCheckSkeleton";
import { API_BASE_URL, API_URL } from "../utils/api";

const BACKEND_URL = API_BASE_URL;
const API = API_URL;

const DonationsPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Main view toggle: 'healthcheck' or 'notice'
  const [mainView, setMainView] = useState('healthcheck');
  
  // Health Check State
  const [partySearch, setPartySearch] = useState("");
  const [donationDate, setDonationDate] = useState("");
  const [healthCheckResult, setHealthCheckResult] = useState(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  
  // Autocomplete State
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Notice Analysis State
  const [noticeText, setNoticeText] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [extractedInfo, setExtractedInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedPath, setSelectedPath] = useState(null);
  const [showPathSelection, setShowPathSelection] = useState(true);
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  
  // Database Stats State
  const [dbStats, setDbStats] = useState(null);

  const tabs = [
    { id: "summary", label: "Summary", icon: ClipboardList },
    { id: "analysis", label: "Analysis", icon: FileSearch },
    { id: "timeline", label: "Timeline", icon: CalendarClock },
    { id: "action", label: "Action", icon: ListTodo },
    { id: "docs", label: "Docs", icon: FolderOpen }
  ];

  // Debounce timeout ref
  const searchTimeoutRef = useRef(null);
  
  // Debounced search for autocomplete
  const debounceSearch = useCallback((query) => {
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API}/party-search?q=${encodeURIComponent(query)}&limit=8`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  // Handle party search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setPartySearch(value);
    setSelectedSuggestionIndex(-1);
    debounceSearch(value);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setPartySearch(suggestion.name);
    setSuggestions([]);
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Enter' && selectedSuggestionIndex < 0) {
      // Close dropdown and allow form submission
      setShowSuggestions(false);
      setSuggestions([]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Database Stats on mount
  useEffect(() => {
    const fetchDbStats = async () => {
      try {
        const response = await fetch(`${API}/database-stats`);
        if (response.ok) {
          const data = await response.json();
          setDbStats(data);
        }
      } catch (error) {
        console.error("Failed to fetch database stats:", error);
      }
    };
    fetchDbStats();
  }, []);

  // Health Check Function
  const performHealthCheck = async () => {
    if (!partySearch.trim()) {
      toast.error("Please enter Party Name or PAN");
      return;
    }

    setIsCheckingHealth(true);
    setHealthCheckResult(null);
    setShowSuggestions(false);

    try {
      const response = await fetch(`${API}/party-health-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          party_name_or_pan: partySearch,
          donation_date: donationDate || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        setHealthCheckResult(result);
        toast.success("Party status retrieved successfully!");
      } else {
        throw new Error("Failed to check party status");
      }
    } catch (error) {
      console.error("Health check error:", error);
      toast.error("Failed to check party status. Please try again.");
    } finally {
      setIsCheckingHealth(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }

    setUploadedFile(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/extract-pdf`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.text) {
          setNoticeText(data.text);
          toast.success("PDF text extracted successfully");
        }
      }
    } catch (error) {
      console.error("PDF extraction error:", error);
    }
  };

  // Notice Analysis
  const analyzeNotice = async () => {
    if (!noticeText.trim() && !uploadedFile) {
      toast.error("Please upload a notice PDF or enter notice text");
      return;
    }

    setIsLoading(true);
    setAnalysisResult(null);
    setExtractedInfo(null);
    setSelectedPath(null);
    setShowPathSelection(true);
    setLeadSubmitted(false);

    try {
      const formData = new FormData();
      
      if (uploadedFile) {
        formData.append('file', uploadedFile);
      }
      formData.append('notice_text', noticeText);
      formData.append('session_id', `donation-${Date.now()}`);
      
      const response = await fetch(`${API}/analyze-donation-notice`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.structured && result.data) {
          setAnalysisResult(result.data);
          if (result.data.summary) {
            setExtractedInfo(result.data.summary);
          }
          toast.success("Notice analyzed successfully!");
        } else if (result.analysis) {
          setAnalysisResult({ raw_response: result.analysis });
          toast.success("Notice analyzed!");
        }
      } else {
        throw new Error("Analysis failed");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze notice. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePathSelect = (path) => {
    setSelectedPath(path);
    setShowPathSelection(false);
  };

  const handleLeadSubmit = async () => {
    if (!leadForm.phone && !leadForm.email) {
      toast.error("Please provide phone number or email");
      return;
    }

    setIsSubmittingLead(true);
    
    try {
      await fetch(`${API}/submit-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadForm,
          path: selectedPath,
          notice_summary: extractedInfo?.in_simple_terms || noticeText.slice(0, 200),
          claim_amount: extractedInfo?.claim_amount || "Not specified",
          source: "political_donation_tool"
        })
      });
      
      setLeadSubmitted(true);
      toast.success("Thank you! Our expert will contact you shortly.");
    } catch (error) {
      setLeadSubmitted(true);
      toast.success("Thank you! Our expert will contact you shortly.");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const formatContent = (text) => {
    if (!text) return "";
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/## (.*?)(\n|$)/g, '<h3 class="text-cyan-400 font-semibold mt-4 mb-2">$1</h3>')
      .replace(/\n/g, '<br/>');
  };

  // Health Check Result Component
  const renderHealthCheckResult = () => {
    if (!healthCheckResult) return null;

    const resultData = healthCheckResult.data;
    const source = healthCheckResult.source;

    if (resultData?.raw_response) {
      return (
        <div className="space-y-4">
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <div className="text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatContent(resultData.raw_response) }} />
          </div>
          {renderDisclaimers()}
        </div>
      );
    }

    const status = resultData?.status || 'Unknown';
    const isActive = status.toLowerCase() === 'active';
    const isInactive = status.toLowerCase() === 'inactive';
    const isDeregistered = status.toLowerCase() === 'deregistered';

    return (
      <div className="space-y-4">
        {/* Data Freshness Tag - Shows when data was last updated */}
        {resultData?.data_freshness && (
          <div className="flex items-center justify-between bg-slate-800/50 rounded-xl p-3 border border-slate-700">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">
                {resultData.data_freshness.tag}
              </span>
            </div>
            <span className="text-slate-500 text-xs">
              {resultData.data_freshness.source_count} Official ECI PDFs
            </span>
          </div>
        )}

        {/* Party Header with Logo */}
        <div className={`rounded-2xl p-6 border ${
          isActive ? 'bg-green-500/10 border-green-500/30' :
          isInactive ? 'bg-amber-500/10 border-amber-500/30' :
          'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center gap-4">
            {/* Party Symbol/Logo */}
            {resultData?.symbol_image ? (
              <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden border-2 border-white/20">
                <img 
                  src={resultData.symbol_image} 
                  alt={resultData.symbol_name || 'Party Symbol'}
                  className="w-16 h-16 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-16 h-16 items-center justify-center">
                  {isActive ? <ShieldCheck className="w-10 h-10 text-green-400" /> :
                   isInactive ? <ShieldAlert className="w-10 h-10 text-amber-400" /> :
                   <ShieldX className="w-10 h-10 text-red-400" />}
                </div>
              </div>
            ) : (
              <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center text-4xl">
                {/* Display emoji symbol if available */}
                {resultData?.symbol_emoji ? (
                  <span>{resultData.symbol_emoji}</span>
                ) : (
                  isActive ? <ShieldCheck className="w-12 h-12 text-green-400" /> :
                  isInactive ? <ShieldAlert className="w-12 h-12 text-amber-400" /> :
                  <ShieldX className="w-12 h-12 text-red-400" />
                )}
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white">{resultData?.party_name || partySearch}</h3>
              {resultData?.short_name && (
                <p className="text-slate-400 text-sm">{resultData.short_name}</p>
              )}
              {resultData?.symbol_name && (
                <p className="text-slate-500 text-xs mt-1">
                  Symbol: {resultData.symbol_emoji && <span className="mr-1">{resultData.symbol_emoji}</span>}
                  {resultData.category === 'rupp' ? 'Not Available' : resultData.symbol_name}
                </p>
              )}
              <p className={`text-lg font-semibold mt-1 ${
                isActive ? 'text-green-400' : isInactive ? 'text-amber-400' : 'text-red-400'
              }`}>
                Status: {status}
              </p>
              {resultData?.type && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                  {resultData.type}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Data Source Badge - 100% ECI Official */}
        <div className="flex flex-col gap-2">
          {source && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Info className="w-3 h-3" />
              <span>{resultData?.data_source?.source_attribution || 'Source: Official ECI Database'}</span>
            </div>
          )}
          
          {/* ECI Audit Disclaimer (Standard for all results) */}
          {resultData?.audit_disclaimer && (
            <div className="bg-emerald-500/10 rounded-xl p-3 border border-emerald-500/30">
              <p className="text-emerald-300 text-xs flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-400" />
                <span>
                  TaxSetu AI has identified this entry from official ECI records. Please cross-verify from ECI Portal: {' '}
                  <a href="https://www.eci.gov.in/list-of-political-parties" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-300">
                    https://www.eci.gov.in/list-of-political-parties
                  </a>
                </span>
              </p>
            </div>
          )}
          
          {/* Delisting Notice for Delisted/Inactive Parties */}
          {(resultData?.category === 'delisted' || resultData?.category === 'inactive') && resultData?.eci_evidence?.delisting_phase && (
            <div className="bg-red-500/10 rounded-xl p-3 border border-red-500/30">
              <p className="text-red-300 text-xs flex items-start gap-2">
                <AlertOctagon className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-400" />
                <span>
                  <strong>⚠️ DELISTED PARTY:</strong> {resultData.eci_evidence.delisting_phase}. 
                  Reason: {resultData.eci_evidence.delisting_reason || 'Non-compliance with ECI regulations'}. 
                  Donations to delisted parties do NOT qualify for 80GGC/80GGB deductions.
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Status Matrix Table */}
        <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-cyan-400" />
            Data Matrix
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Parameter</th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Value</th>
                </tr>
              </thead>
              <tbody>
                {/* Symbol Row */}
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 px-4 text-slate-300">Symbol</td>
                  <td className="py-3 px-4 text-white">
                    <span className="text-2xl mr-2">{resultData?.symbol_emoji || '🗳️'}</span>
                    <span className="text-slate-400">
                      {resultData?.category === 'rupp' ? 'Not Available' : (resultData?.symbol_name || 'N/A')}
                    </span>
                  </td>
                </tr>
                {/* Party Name */}
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 px-4 text-slate-300">Party Name</td>
                  <td className="py-3 px-4 text-white font-medium">{resultData?.party_name || partySearch}</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 px-4 text-slate-300">Current Status</td>
                  <td className="py-3 px-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isActive ? 'bg-green-500/20 text-green-400' :
                      isInactive ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {status}
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 px-4 text-slate-300">Valid Till Date</td>
                  <td className="py-3 px-4 text-white">
                    {isActive ? 'N/A - Currently Active' : (resultData?.valid_till || 'Check ECI Delisting Order')}
                  </td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 px-4 text-slate-300">PAN</td>
                  <td className="py-3 px-4">
                    <div>
                      <span className={`font-mono ${resultData?.category === 'rupp' ? 'text-amber-400' : 'text-white'}`}>
                        {resultData?.category === 'rupp' ? 'Could not be Verified' : (resultData?.pan || 'Could not be Verified')}
                      </span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-3 px-4 text-slate-300">ECI Registration No.</td>
                  <td className="py-3 px-4 text-white font-mono text-sm">
                    {resultData?.category === 'rupp' ? 'Could not be Verified' : (resultData?.eci_registration || 'Could not be Verified')}
                  </td>
                </tr>
                {resultData?.state_of_registration && (
                  <tr className="border-b border-slate-700/50">
                    <td className="py-3 px-4 text-slate-300">State of Registration</td>
                    <td className="py-3 px-4 text-white">{resultData.state_of_registration}</td>
                  </tr>
                )}
                {resultData?.headquarters && (
                  <tr className="border-b border-slate-700/50">
                    <td className="py-3 px-4 text-slate-300">Headquarters</td>
                    <td className="py-3 px-4 text-white">{resultData.headquarters}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Source Evidence Section */}
        {resultData?.eci_evidence && (
          <div className="bg-purple-500/10 rounded-2xl p-5 border border-purple-500/30">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-purple-400" />
              Source Evidence (Official ECI Record)
            </h3>
            
            {/* Data Source */}
            <div className="space-y-3">
              <div className="bg-slate-900/50 rounded-xl p-3">
                <p className="text-slate-500 text-xs">Data Source</p>
                <a 
                  href="https://www.eci.gov.in/list-of-political-parties" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 text-sm mt-1 flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Official Website: https://www.eci.gov.in/list-of-political-parties
                </a>
              </div>
              
              {/* Document Name - Only show for RUPPs and Delisted parties */}
              {(resultData.category === 'rupp' || resultData.category === 'delisted') ? (
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Document Name</p>
                  <p className="text-white text-sm mt-1">Commission's Notification dated 10.10.2025 containing list of RUPPs, Delisted RUPPs.</p>
                  <p className="text-slate-400 text-xs mt-2">
                    → Go to the ECI portal above and look for this PDF document, then navigate to Page {resultData.eci_evidence.page_number}, S.No. {resultData.eci_evidence.serial_number}
                  </p>
                </div>
              ) : (
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Document Name</p>
                  <p className="text-slate-400 text-sm mt-1">N/A - National/State Party details available directly on ECI portal</p>
                </div>
              )}
              
              {/* Document Reference */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Document Reference</p>
                  <p className="text-white font-bold text-lg mt-1">
                    {(resultData.category === 'rupp' || resultData.category === 'delisted') 
                      ? (resultData.eci_evidence.page_number || 'N/A')
                      : 'N/A'}
                  </p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Sr. Number</p>
                  <p className="text-white font-bold text-lg mt-1">
                    {(resultData.category === 'rupp' || resultData.category === 'delisted') 
                      ? (resultData.eci_evidence.serial_number || 'N/A')
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form 24A Compliance - REMOVED per user request */}

        {/* Status History Section - Shows full timeline for parties that were delisted/relisted */}
        {resultData?.status_history && resultData.status_history.length > 0 && (
          <div className="bg-purple-500/10 rounded-2xl p-5 border border-purple-500/30">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-400" />
              Status History (Full Timeline)
            </h3>
            <div className="space-y-4">
              {resultData.status_history.map((history, idx) => (
                <div key={idx} className={`p-4 rounded-xl border ${
                  history.status === 'Delisted' ? 'bg-red-500/10 border-red-500/30' :
                  history.status === 'Active' ? 'bg-green-500/10 border-green-500/30' :
                  history.status.includes('Disputed') ? 'bg-amber-500/10 border-amber-500/30' :
                  'bg-slate-800/50 border-slate-700'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      history.status === 'Delisted' ? 'bg-red-500/20 text-red-400' :
                      history.status === 'Active' ? 'bg-green-500/20 text-green-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {history.status}
                    </span>
                    <span className="text-slate-400 text-sm">
                      📅 Effective: {history.effective_date}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm mb-3">{history.reason}</p>
                  
                  {/* PDF Reference for this status */}
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-cyan-400 text-xs">📄 PDF Reference:</p>
                        <p className="text-white text-sm font-medium">{history.pdf_filename}</p>
                        <p className="text-slate-500 text-xs">Page: {history.page_number}, S.No: {history.serial_number}</p>
                      </div>
                      <a 
                        href={history.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm flex items-center gap-1"
                      >
                        <FileDown className="w-4 h-4" />
                        Download PDF
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Previous Status Evidence - For parties with history */}
        {resultData?.previous_status_evidence && !resultData?.status_history && (
          <div className="bg-amber-500/10 rounded-2xl p-5 border border-amber-500/30">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Previous Status Record
            </h3>
            <div className="bg-slate-900/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-500/20 text-amber-400">
                  {resultData.previous_status_evidence.previous_status}
                </span>
                <span className="text-slate-400 text-sm">
                  📅 As of: {resultData.previous_status_evidence.notification_date}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-slate-500 text-xs">Page Number</p>
                  <p className="text-white font-medium">{resultData.previous_status_evidence.page_number}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Serial Number</p>
                  <p className="text-white font-medium">{resultData.previous_status_evidence.serial_number}</p>
                </div>
              </div>
              <a 
                href={resultData.previous_status_evidence.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl hover:bg-amber-500/15 transition-colors"
              >
                <FileDown className="w-5 h-5 text-amber-400" />
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">{resultData.previous_status_evidence.pdf_filename}</p>
                  <p className="text-xs text-slate-500">Previous status evidence</p>
                </div>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">PDF</span>
              </a>
            </div>
          </div>
        )}

        {/* Donation Date Verification */}
        {donationDate && (
          <div className={`rounded-2xl p-5 border ${
            resultData?.donation_valid ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
          }`}>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-400" />
              Donation Date Verification
            </h3>
            <div className="flex items-center gap-3">
              {resultData?.donation_valid ? (
                <>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-green-400 font-semibold">Party was ACTIVE on {donationDate}</p>
                    <p className="text-green-300/70 text-sm">{resultData?.donation_verification || 'Your donation date falls within the party\'s active registration period'}</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertOctagon className="w-8 h-8 text-red-400" />
                  <div>
                    <p className="text-red-400 font-semibold">⚠️ Verification Required for {donationDate}</p>
                    <p className="text-red-300/70 text-sm">{resultData?.donation_verification || 'Please verify with official ECI records before proceeding'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Official Sources with Direct PDF Links */}
        <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileDown className="w-5 h-5 text-cyan-400" />
            Official Documents - Download PDFs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* 10th Oct 2025 - Registered and Delisted Parties */}
            <a 
              href="https://customer-assets.emergentagent.com/job_taxguru-5/artifacts/yh29n3nh_8.%20Registered%20and%20Delisted%20Parties_10102025.pdf"
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl hover:bg-red-500/15 transition-colors"
            >
              <FileDown className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-white font-medium text-sm">Registered & Delisted (10.10.2025)</p>
                <p className="text-xs text-slate-500">TABLE-1(A) & TABLE-1(B)</p>
              </div>
              <span className="ml-auto px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">PDF</span>
            </a>
            {/* Delisted Parties Phase 2 - 19th Sept 2025 */}
            <a 
              href="https://customer-assets.emergentagent.com/job_taxguru-5/artifacts/1gcn8ek3_7.%20Delisted%20Parties_19092025.pdf"
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl hover:bg-red-500/15 transition-colors"
            >
              <FileDown className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-white font-medium text-sm">Delisted Parties (19.09.2025)</p>
                <p className="text-xs text-slate-500">ECI Cleanup Phase 2</p>
              </div>
              <span className="ml-auto px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">PDF</span>
            </a>
            {/* Delisted Parties Phase 1 - 8th Aug 2025 */}
            <a 
              href="https://customer-assets.emergentagent.com/job_taxguru-5/artifacts/6.%20Delisted%20Parties_08082025.pdf"
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl hover:bg-red-500/15 transition-colors"
            >
              <FileDown className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-white font-medium text-sm">Delisted Parties (08.08.2025)</p>
                <p className="text-xs text-slate-500">ECI Cleanup Phase 1</p>
              </div>
              <span className="ml-auto px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">PDF</span>
            </a>
          </div>
        </div>

        {/* Party Website */}
        {resultData?.official_website && (
          <a 
            href={resultData.official_website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl hover:bg-purple-500/15 transition-colors"
          >
            <Link2 className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-white font-medium">Official Party Website</p>
              <p className="text-xs text-purple-300/70">{resultData.official_website}</p>
            </div>
            <ExternalLink className="w-4 h-4 text-purple-400 ml-auto" />
          </a>
        )}

        {/* Disclaimers */}
        {renderDisclaimers()}
      </div>
    );
  };

  // Disclaimers Component
  const renderDisclaimers = () => {
    // Get resultData from healthCheckResult if available
    const disclaimerData = healthCheckResult?.data;
    
    return (
    <div className="space-y-3">
      {/* Database Stats - Shows total parties searchable */}
      {dbStats && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl p-4 border border-emerald-500/30">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-emerald-400 font-semibold text-sm">📊 Database Coverage</p>
                <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">
                  {dbStats.total?.toLocaleString()} Parties Searchable
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                  <p className="text-lg font-bold text-white">{dbStats.national_parties}</p>
                  <p className="text-xs text-slate-400">National</p>
                </div>
                <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                  <p className="text-lg font-bold text-white">{dbStats.state_parties}</p>
                  <p className="text-xs text-slate-400">State</p>
                </div>
                <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                  <p className="text-lg font-bold text-emerald-400">{dbStats.active_rupps?.toLocaleString()}</p>
                  <p className="text-xs text-slate-400">Active RUPPs</p>
                </div>
                <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                  <p className="text-lg font-bold text-red-400">{dbStats.delisted_parties}</p>
                  <p className="text-xs text-slate-400">Delisted</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs mt-2">
                As per <span className="text-emerald-300">Election Commission's Notification dated 10.10.2025</span> containing list of RUPPs & Delisted RUPPs.
              </p>
              <a 
                href="https://www.eci.gov.in/list-of-political-parties" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mt-1 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Cross-verify at ECI Official Portal
              </a>
            </div>
          </div>
        </div>
      )}

      {/* AI Disclaimer */}
      <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-semibold text-sm">⚠️ AI & Accuracy Disclaimer</p>
            <p className="text-slate-300 text-xs mt-1">
              TaxSetu AI can make mistakes. Please cross-verify all information with Official Records (ECI, CBDT) before taking any action.
            </p>
          </div>
        </div>
      </div>

      {/* Liability Clause - Use API-provided disclaimer if available */}
      <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
        <div className="flex items-start gap-3">
          <AlertOctagon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-semibold text-sm">🚫 Liability Disclaimer</p>
            <p className="text-slate-300 text-xs mt-1">
              {disclaimerData?.liability_disclaimer || 
                "TaxSetu AI is an informational tool only. It does not influence or recommend donations. Any financial or legal liability arising from future or past donations rests solely with the individual user. TaxSetu shall not be held responsible for any adverse tax consequences or legal proceedings."}
            </p>
          </div>
        </div>
      </div>
    </div>
  )};


  // Tab content renderer (for notice analysis)
  const renderTabContent = () => {
    if (!analysisResult) return null;

    if (analysisResult.raw_response) {
      return (
        <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
          <div className="text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatContent(analysisResult.raw_response) }} />
        </div>
      );
    }

    switch (activeTab) {
      case "summary":
        const summary = analysisResult.summary || {};
        const riskColor = summary.risk_level === 'HIGH' ? 'red' : summary.risk_level === 'MEDIUM' ? 'amber' : 'green';
        
        return (
          <div className="space-y-4">
            {summary.risk_level === 'HIGH' && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-red-400 font-semibold">HIGH RISK: Political Donation Claim Detected</p>
                  <p className="text-red-300/80 text-sm mt-1">Department is questioning the genuineness of your 80GGC/80GGB claim</p>
                </div>
              </div>
            )}
            
            <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-cyan-400" />
                Executive Summary
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Notice Type</p>
                  <p className="text-white font-medium mt-1">{summary.notice_type || "Political Donation Inquiry"}</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Section</p>
                  <p className="text-pink-400 font-medium mt-1">{summary.section || "80GGC/80GGB"}</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Assessment Year</p>
                  <p className="text-white font-medium mt-1">{summary.assessment_year || "N/A"}</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Claim Amount</p>
                  <p className="text-amber-400 font-medium mt-1">{summary.claim_amount || "N/A"}</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Risk Level</p>
                  <p className={`text-${riskColor}-400 font-bold mt-1`}>{summary.risk_level || "HIGH"}</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">DIN</p>
                  <p className="text-white font-mono text-sm mt-1">{summary.din_number || "N/A"}</p>
                </div>
              </div>
              
              {summary.in_simple_terms && (
                <div className="mt-4 p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                  <p className="text-amber-400 text-sm font-medium mb-1">In Simple Terms:</p>
                  <p className="text-slate-300 text-sm">{summary.in_simple_terms}</p>
                </div>
              )}
            </div>
          </div>
        );

      case "analysis":
        const analysis = analysisResult.analysis || {};
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-cyan-400" />
              Detailed Analysis
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-slate-900/50 rounded-xl">
                <p className="text-pink-400 font-medium mb-1">Department Concerns</p>
                <p className="text-slate-300 text-sm">{analysis.department_concerns || "N/A"}</p>
              </div>
              <div className="p-3 bg-slate-900/50 rounded-xl">
                <p className="text-amber-400 font-medium mb-1">Potential Implications</p>
                <p className="text-slate-300 text-sm">{analysis.potential_implications || "N/A"}</p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/30">
                <p className="text-green-400 font-medium mb-1">Strengths</p>
                <p className="text-slate-300 text-sm">{analysis.strengths || "N/A"}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/30">
                <p className="text-red-400 font-medium mb-1">Weaknesses</p>
                <p className="text-slate-300 text-sm">{analysis.weaknesses || "N/A"}</p>
              </div>
            </div>
          </div>
        );

      case "timeline":
        const timeline = analysisResult.timeline || {};
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-amber-400" />
              Timeline & Deadlines
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                <Calendar className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-white font-medium">Notice Date</p>
                  <p className="text-sm text-slate-400">{timeline.notice_date || "As per notice"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-xl border border-red-500/30">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-white font-medium">Response Deadline</p>
                  <p className="text-sm text-red-300">{timeline.response_deadline || "15-30 days from notice"}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "action":
        const actions = analysisResult.action_items || [];
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-green-400" />
              Action Items
            </h3>
            <ul className="space-y-2">
              {actions.length > 0 ? actions.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item}</span>
                </li>
              )) : (
                <li className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-slate-300">Gather donation receipt and party registration proof</span>
                </li>
              )}
            </ul>
          </div>
        );

      case "docs":
        const docs = analysisResult.documents_required || [];
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-purple-400" />
              Documents Required
            </h3>
            <div className="space-y-2">
              {docs.length > 0 ? docs.map((doc, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <FileText className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-white font-medium">{doc.document}</p>
                    <p className="text-sm text-slate-400">{doc.purpose}</p>
                  </div>
                </div>
              )) : (
                <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <span className="text-slate-300">Donation Receipt from Political Party</span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Path Selection Component
  const renderPathSelection = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">Choose Your Resolution Path</h3>
        <p className="text-slate-400 text-sm">Select how you want to proceed with this notice</p>
      </div>

      {/* Path A */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        onClick={() => handlePathSelect('A')}
        className="w-full bg-green-500/10 rounded-2xl p-5 border border-green-500/30 hover:bg-green-500/20 transition-all text-left group"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Shield className="w-7 h-7 text-green-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-green-400 font-bold text-lg">Path A: Draft Legal Reply</h4>
            <p className="text-green-300/70 text-sm mt-1">Fight the case with professional defense if your donation was genuine</p>
          </div>
          <ArrowRight className="w-6 h-6 text-green-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </motion.button>

      {/* Path B */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        onClick={() => handlePathSelect('B')}
        className="w-full bg-amber-500/10 rounded-2xl p-5 border border-amber-500/30 hover:bg-amber-500/20 transition-all text-left group"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
            <UserCheck className="w-7 h-7 text-amber-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-amber-400 font-bold text-lg">Path B: Consult Expert CA</h4>
            <p className="text-amber-300/70 text-sm mt-1">Get professional guidance from our tax litigation experts</p>
          </div>
          <ArrowRight className="w-6 h-6 text-amber-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </motion.button>

      {/* Path C */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        onClick={() => handlePathSelect('C')}
        className="w-full bg-red-500/10 rounded-2xl p-5 border border-red-500/30 hover:bg-red-500/20 transition-all text-left group"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
            <FileEdit className="w-7 h-7 text-red-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-red-400 font-bold text-lg">Path C: File ITR-U for Safety</h4>
            <p className="text-red-300/70 text-sm mt-1">Withdraw claim via Updated Return u/s 139(8A) to avoid penalties</p>
          </div>
          <ArrowRight className="w-6 h-6 text-red-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </motion.button>
    </div>
  );

  // Path A - Draft Reply
  const renderDraftReply = () => {
    const paths = analysisResult?.resolution_paths || {};
    const draftReply = paths.path_a?.draft_reply || `To,
The Assessing Officer,
Income Tax Department,
[Jurisdiction as per Notice]

SUBJECT: Reply to Notice regarding Section 80GGC Claim - ${extractedInfo?.assessment_year || 'AY [Year]'} - PAN: [Your PAN]
Ref: DIN: ${extractedInfo?.din_number || '[Notice DIN]'}

Respected Sir/Madam,

1. PRELIMINARY SUBMISSIONS:
The undersigned is a bona fide taxpayer with impeccable compliance record. The assessee has been regularly filing returns of income and discharging all statutory tax obligations within the prescribed timelines.

2. REGARDING THE 80GGC CLAIM:
The donation of ${extractedInfo?.claim_amount || 'Rs. [Amount]'} was made to a registered political party in good faith. The following documents are submitted in support:
   a) Original donation receipt from the political party
   b) Bank statement showing debit entry
   c) Political party registration certificate from Election Commission
   d) PAN of the political party

3. LEGAL POSITION:
Section 80GGC allows 100% deduction for donations made to political parties registered under Section 29A of the Representation of the People Act, 1951. The donation was made through banking channels as mandated by law.

4. FACTUAL SUBMISSIONS:
   - The donation was made through [cheque/bank transfer/digital mode]
   - The political party is duly registered with the Election Commission of India
   - The donation receipt contains all prescribed particulars under Rule 17C

PRAYER:
In view of the above submissions and documentary evidence, it is most respectfully prayed that Your Honour may kindly:
(a) Accept the genuineness of the 80GGC claim
(b) Allow the deduction as claimed in the return of income
(c) Drop the proceedings initiated vide the impugned notice

UNDERTAKING:
The undersigned undertakes to appear and cooperate in the proceedings as may be required.

Respectfully submitted,

[Your Name]
PAN: [Your PAN]
Address: [Your Address]
Date: [Date]
Place: [City]

Enclosures:
1. Copy of Notice under reply
2. Original Donation Receipt
3. Bank Statement
4. Party Registration Certificate
5. PAN Card of Political Party`;

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setSelectedPath(null); setShowPathSelection(true); }}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Path Selection
        </button>

        <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-green-400" />
            <div>
              <h3 className="text-green-400 font-bold">Path A: Draft Legal Reply</h3>
              <p className="text-green-300/70 text-sm">Professional defense drafted by Senior Litigation Expert</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <PenTool className="w-5 h-5 text-cyan-400" />
            Draft Notice Reply
          </h3>
          
          <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm max-h-[400px] overflow-y-auto">
            <pre className="text-slate-300 whitespace-pre-wrap leading-relaxed">
              {draftReply}
            </pre>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button 
              className="flex-1 bg-slate-700/50 text-white hover:bg-slate-600/50 border border-slate-600"
              onClick={() => {
                navigator.clipboard.writeText(draftReply);
                toast.success("Draft copied to clipboard!");
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Draft
            </Button>
            <Button 
              className="flex-1 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
              onClick={() => {
                const blob = new Blob([draftReply], { type: 'application/msword' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Notice_Reply_80GGC_${Date.now()}.doc`;
                link.click();
                toast.success("Draft downloaded!");
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download as Word
            </Button>
          </div>
        </div>

        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
          <p className="text-amber-400 text-sm">
            ⚠️ <strong>Important:</strong> This is an AI-generated draft. Please review and customize it based on your specific facts before submission.
          </p>
        </div>
      </div>
    );
  };

  // Lead Form Component
  const renderLeadForm = () => {
    const isPathB = selectedPath === 'B';
    const color = isPathB ? 'amber' : 'red';
    const Icon = isPathB ? UserCheck : FileEdit;
    const title = isPathB ? 'Consult Expert CA' : 'File ITR-U for Safety';
    const description = isPathB 
      ? 'Our tax litigation experts will guide you through the process'
      : 'Our experts will help you file Updated Return u/s 139(8A)';

    if (leadSubmitted) {
      return (
        <div className="space-y-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-green-500/10 rounded-2xl p-8 border border-green-500/30 text-center"
          >
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
            <p className="text-slate-300">Our expert consultant will contact you within 24 hours.</p>
            <p className="text-slate-500 text-sm mt-2">You can also reach us at: support@taxsetu.ai</p>
          </motion.div>
          
          <Button
            onClick={() => { setSelectedPath(null); setShowPathSelection(true); setLeadSubmitted(false); }}
            className="w-full bg-slate-700/50 text-white hover:bg-slate-600/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Path Selection
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setSelectedPath(null); setShowPathSelection(true); }}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Path Selection
        </button>

        <div className={`bg-${color}-500/10 rounded-xl p-4 border border-${color}-500/30`}>
          <div className="flex items-center gap-3">
            <Icon className={`w-6 h-6 text-${color}-400`} />
            <div>
              <h3 className={`text-${color}-400 font-bold`}>Path {selectedPath}: {title}</h3>
              <p className={`text-${color}-300/70 text-sm`}>{description}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-pink-400" />
            Request Expert Consultation
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Your Name</label>
              <input
                type="text"
                value={leadForm.name}
                onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                placeholder="Enter your name"
                className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Phone Number *</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  placeholder="+91 98765 43210"
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Email Address *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                  placeholder="your@email.com"
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Additional Message (Optional)</label>
              <textarea
                value={leadForm.message}
                onChange={(e) => setLeadForm({...leadForm, message: e.target.value})}
                placeholder="Briefly describe your situation..."
                rows={3}
                className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50 resize-none"
              />
            </div>

            <Button
              onClick={handleLeadSubmit}
              disabled={isSubmittingLead || (!leadForm.phone && !leadForm.email)}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl py-3"
            >
              {isSubmittingLead ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Request Callback
                </>
              )}
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 mt-4 text-center">
            By submitting, you agree to be contacted by our tax experts. Your information is secure.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-30">
        <div className="w-full px-4 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-semibold text-white">Political Donation Help</h1>
                <p className="text-xs text-slate-500">Health Check & Notice Shield</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate('/notice')}
              className="text-slate-400 hover:text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              IT Notice
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main View Toggle */}
      <div className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-2 flex gap-2">
          <button
            onClick={() => setMainView('healthcheck')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
              mainView === 'healthcheck' 
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Party Health Check
            </div>
          </button>
          <button
            onClick={() => setMainView('notice')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
              mainView === 'notice' 
                ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileWarning className="w-5 h-5" />
              Notice Shield
            </div>
          </button>
        </div>
      </div>

      {/* Health Check View */}
      <AnimatePresence mode="wait">
        {mainView === 'healthcheck' ? (
          <motion.div
            key="healthcheck"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col lg:flex-row h-[calc(100vh-120px)]"
          >
            {/* Left Panel - Search & Stats */}
            <div className="w-full lg:w-1/3 border-r border-slate-800 p-6 overflow-y-auto">
              {/* Search Section */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Search className="w-5 h-5 text-cyan-400" />
                  Political Donation Health Check
                </h2>
                <p className="text-slate-400 text-sm mb-4">
                  Verify political party registration status before making donations
                </p>
                
                <div className="space-y-4">
                  {/* Party Search with Autocomplete */}
                  <div className="relative" ref={searchInputRef}>
                    <label className="block text-sm text-slate-400 mb-2">Party Name or PAN *</label>
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="text"
                        value={partySearch}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => partySearch.length >= 2 && setShowSuggestions(true)}
                        placeholder="e.g., BJP, Congress, AAP..."
                        className="w-full bg-slate-900/80 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                        data-testid="party-search-input"
                        onBlur={() => {
                          setTimeout(() => {
                            setShowSuggestions(false);
                          }, 200);
                        }}
                      />
                      {isSearching && (
                        <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400 animate-spin" />
                      )}
                    </div>
                    
                    {/* Autocomplete Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-2 bg-slate-900 border border-slate-700 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionSelect(suggestion)}
                            onMouseDown={(e) => e.preventDefault()}
                            className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                              index === selectedSuggestionIndex 
                                ? 'bg-cyan-500/20' 
                                : 'hover:bg-slate-800'
                            }`}
                            data-testid={`party-suggestion-${index}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 text-xl">
                              {suggestion.symbol_emoji || '🗳️'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium text-sm truncate">{suggestion.name}</p>
                              <p className="text-xs text-slate-500">{suggestion.short_name} • {suggestion.type}</p>
                            </div>
                            <span className={`text-xs ${suggestion.status === 'Active' ? 'text-green-400' : 'text-red-400'}`}>
                              {suggestion.status}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Date of Donation (Optional)
                    </label>
                    <input
                      type="date"
                      value={donationDate}
                      onChange={(e) => setDonationDate(e.target.value)}
                      className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50"
                      data-testid="donation-date-input"
                    />
                  </div>

                  <Button
                    onClick={performHealthCheck}
                    disabled={isCheckingHealth || !partySearch.trim()}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl py-3"
                    data-testid="check-party-status-btn"
                  >
                    {isCheckingHealth ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5 mr-2" />
                        Check Party Status
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Data Current Info */}
              <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-slate-400">Data current as of:</span>
                  <span className="text-white font-medium">10th October 2025</span>
                </div>
              </div>

              {/* Database Statistics */}
              {dbStats && (
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl p-4 border border-emerald-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 font-semibold text-sm">Database Coverage</span>
                    <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full ml-auto">
                      {dbStats.total?.toLocaleString()} Parties
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                      <p className="text-lg font-bold text-white">{dbStats.national_parties}</p>
                      <p className="text-xs text-slate-400">National</p>
                    </div>
                    <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                      <p className="text-lg font-bold text-white">{dbStats.state_parties}</p>
                      <p className="text-xs text-slate-400">State</p>
                    </div>
                    <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                      <p className="text-lg font-bold text-emerald-400">{dbStats.active_rupps?.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">Active RUPPs</p>
                    </div>
                    <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                      <p className="text-lg font-bold text-red-400">{dbStats.delisted_parties}</p>
                      <p className="text-xs text-slate-400">Delisted</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Results */}
            <div className="w-full lg:w-2/3 p-6 overflow-y-auto">
              {isCheckingHealth ? (
                <PartyHealthCheckSkeleton />
              ) : healthCheckResult ? (
                renderHealthCheckResult()
              ) : (
                /* Default State - Instructions */
                <div className="h-full flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-6">
                      <Heart className="w-10 h-10 text-pink-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      Verify Before You Donate
                    </h3>
                    <p className="text-slate-400 mb-6">
                      Search for any political party to check their registration status and 80GGC/80GGB eligibility. Our database covers 2,267+ parties including National, State, and Registered Unrecognised parties.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <span className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-400">80GGC Deduction</span>
                      <span className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-400">80GGB Deduction</span>
                      <span className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-400">ECI Verified</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          /* Notice Shield View */
          <motion.div
            key="notice"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col lg:flex-row h-[calc(100vh-120px)]"
          >
            {/* Left Panel - Input */}
            <div className="w-full lg:w-1/3 border-r border-slate-800 p-6 overflow-y-auto">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-pink-400" />
                  Upload Notice / Enter Query
                </h2>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-6 border-2 border-dashed border-slate-700 rounded-xl hover:border-pink-500/50 transition-colors mb-4"
                >
                  {uploadedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileUp className="w-8 h-8 text-pink-400" />
                      <div className="text-left">
                        <p className="text-white font-medium">{uploadedFile.name}</p>
                        <p className="text-sm text-slate-500">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                          setNoticeText("");
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <FileUp className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-400">Upload Notice PDF</p>
                    </div>
                  )}
                </button>

                <textarea
                  value={noticeText}
                  onChange={(e) => setNoticeText(e.target.value)}
                  placeholder="Or type/paste your notice content..."
                  className="w-full h-32 bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50 resize-none"
                />
              </div>

              <Button
                onClick={analyzeNotice}
                disabled={isLoading || (!noticeText.trim() && !uploadedFile)}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl py-3"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FileSearch className="w-5 h-5 mr-2" />
                    Analyze Notice
                  </>
                )}
              </Button>
            </div>

            {/* Right Panel - Results */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {analysisResult ? (
                <>
                  {!showPathSelection && (
                    <div className="flex border-b border-slate-800 px-4 overflow-x-auto">
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                            activeTab === tab.id 
                              ? 'border-pink-500 text-pink-400' 
                              : 'border-transparent text-slate-500 hover:text-slate-300'
                          }`}
                        >
                          <tab.icon className="w-4 h-4" />
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <ScrollArea className="flex-1 p-6">
                    {showPathSelection ? (
                      <>
                        {extractedInfo && (
                          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 mb-6">
                            <div className="flex items-center gap-3 mb-2">
                              <AlertTriangle className="w-5 h-5 text-red-400" />
                              <span className="text-red-400 font-semibold">Risk Level: {extractedInfo.risk_level || 'HIGH'}</span>
                            </div>
                            <p className="text-slate-300 text-sm">{extractedInfo.in_simple_terms}</p>
                          </div>
                        )}
                        {renderPathSelection()}
                      </>
                    ) : selectedPath === 'A' ? (
                      renderDraftReply()
                    ) : selectedPath === 'B' || selectedPath === 'C' ? (
                      renderLeadForm()
                    ) : (
                      renderTabContent()
                    )}
                  </ScrollArea>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <FileWarning className="w-20 h-20 text-slate-800 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-600 mb-2">Notice Shield</h3>
                    <p className="text-slate-500 max-w-md">
                      Upload your 80GGC/80GGB notice to get professional analysis with resolution paths.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DonationsPage;
